import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "@phosphor-icons/react";
import { lies, schick } from "../api/client";
import type { Projekt } from "../api/types";
import { Dialog, Feld, Meldung } from "../components/Bausteine";
import { datum } from "../lib/format";
import { useIch } from "../App";
import { usePfad } from "../lib/pfad";

const STATUS: Record<Projekt["status"], string> = {
  vorbereitung: "Vorbereitung",
  ausfuehrung: "Ausführung",
  maengelhaftung: "Mängelhaftung",
  abgeschlossen: "abgeschlossen",
};

export function Projekte() {
  const ich = useIch().data;
  const projekte = useQuery({ queryKey: ["projekte"], queryFn: () => lies<Projekt[]>("/projekte") });
  const [neu, setNeu] = useState(false);
  usePfad([]);

  return (
    <main className="seite">
      <div className="seite-kopf">
        <h1>Projekte</h1>
        <div className="luft" />
        {ich && ich.rolle !== "extern" && (
          <button className="knopf haupt" onClick={() => setNeu(true)}>
            <Plus size={16} weight="bold" /> Projekt anlegen
          </button>
        )}
      </div>
      <Meldung fehler={projekte.error} />
      {projekte.data?.length === 0 && (
        <div className="karte leer">
          {ich?.rolle === "extern"
            ? "Noch kein Projekt freigegeben. Die Einladung kommt vom Büro."
            : "Noch kein Projekt. Das erste lässt sich oben rechts anlegen."}
        </div>
      )}
      <div className="kacheln">
        {projekte.data?.map((p) => (
          <Link key={p.id} to={`/p/${p.kurz}`} className="karte kachel">
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {p.nummer && <span className="chip">{p.nummer}</span>}
              <span className={`chip${p.status === "ausfuehrung" ? " akzent" : ""}`}>{STATUS[p.status]}</span>
            </div>
            <h2>{p.name}</h2>
            <div className="dim">{[p.ort, p.bauherr].filter(Boolean).join(" · ")}</div>
            <div className="zahlen ziffern">
              <span><b>{p.punkte_offen}</b> offene Punkte</span>
              <span><b>{p.maengel_offen}</b> offene Mängel</span>
              {p.letzte_besprechung && <span>zuletzt {datum(p.letzte_besprechung)}</span>}
            </div>
          </Link>
        ))}
      </div>
      <NeuesProjekt offen={neu} zu={() => setNeu(false)} />
    </main>
  );
}

function NeuesProjekt({ offen, zu }: { offen: boolean; zu: () => void }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", nummer: "", ort: "", bauherr: "" });
  const anlegen = useMutation({
    mutationFn: () => schick<Projekt>("/projekte", f),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["projekte"] });
      zu();
      nav(`/p/${p.id}`);
    },
  });
  const setze = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF({ ...f, [k]: e.target.value });
  return (
    <Dialog offen={offen} zu={zu} titel="Projekt anlegen" fuss={<>
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={!f.name.trim() || anlegen.isPending}
              onClick={() => anlegen.mutate()}>Anlegen</button>
    </>}>
      <div className="raster">
        <Feld titel="Name" breit><input className="eingabe" autoFocus value={f.name} onChange={setze("name")} /></Feld>
        <Feld titel="Projektnummer"><input className="eingabe" value={f.nummer} onChange={setze("nummer")} /></Feld>
        <Feld titel="Ort"><input className="eingabe" value={f.ort} onChange={setze("ort")} /></Feld>
        <Feld titel="Bauherr" breit><input className="eingabe" value={f.bauherr} onChange={setze("bauherr")} /></Feld>
      </div>
      <p className="mute">Gliederung und Objektüberwachung werden vorbelegt und lassen sich danach ändern.</p>
      <Meldung fehler={anlegen.error} />
    </Dialog>
  );
}
